import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Board, Column } from '@/entities/board';
import type { MemberRole } from '@/entities/collab';
import { useWorkspaces } from '@/entities/workspace';
import {
  BoardAccessPanel,
  BoardLabelsSection,
  BoardTransferSection,
  OwnershipPanel,
} from '@/features/board-access';
import { BoardDeleteSection, BoardGeneralForm } from '@/features/board-manage/BoardSettingsDialog';
import { can, PERMISSIONS } from '@/shared/lib/permissions';
import { Button, Dialog, Icons } from '@/shared/ui';

import styles from './BoardSettingsDialog.module.css';

type Section = 'general' | 'appearance' | 'labels' | 'access' | 'advanced';

interface Props {
  board: Board;
  columns: Column[];
  role: MemberRole;
  permissions: readonly string[];
  initialSection?: Section;
  onClose: () => void;
  onDeleted: () => void;
  /** Opens the established appearance editor (all customization is kept there). */
  onAppearance?: (() => void) | undefined;
  /** Absent where the export dialog is not available (the catalog): the block is hidden. */
  onExport?: (() => void) | undefined;
}

const ICONS: Record<Section, () => React.JSX.Element> = {
  general: Icons.sliders,
  appearance: Icons.palette,
  labels: Icons.tag,
  access: Icons.key,
  advanced: Icons.layers,
};

/**
 * The board's settings, one coherent surface: General, Appearance, Labels, Access and Advanced
 * (ownership, transfer, export, trash). The Access section is the same component the fast
 * access button opens, so there is one permission editor, not two. Sections appear according to
 * the permissions the server reported.
 */
export function BoardSettingsDialog({
  board,
  columns,
  role,
  permissions,
  initialSection = 'general',
  onClose,
  onDeleted,
  onAppearance,
  onExport,
}: Props) {
  const { t } = useTranslation();
  const workspaces = useWorkspaces();
  const isMember = (workspaces.data ?? []).some((w) => w.id === board.workspace_id);
  const canEdit = can(permissions, PERMISSIONS.boardEditDetails);
  const canAccess =
    can(permissions, PERMISSIONS.boardManageMembers) ||
    can(permissions, PERMISSIONS.boardManagePermissions);
  const canTransfer = can(permissions, PERMISSIONS.boardTransfer) && board.workspace_id !== null;
  const canDelete = can(permissions, PERMISSIONS.boardDelete);

  const sections: Section[] = [
    'general',
    ...(canEdit && onAppearance ? (['appearance'] as const) : []),
    ...(isMember ? (['labels'] as const) : []),
    ...(canAccess ? (['access'] as const) : []),
    'advanced',
  ];
  const [section, setSection] = useState<Section>(
    sections.includes(initialSection) ? initialSection : 'general',
  );

  return (
    <Dialog title={t('boards.settings')} size="xl" onClose={onClose}>
      <div className={styles.layout}>
        <nav className={styles.nav} aria-label={t('boardSettings.sections')}>
          <ul className={`${styles.navList} scroll-strip`}>
            {sections.map((value) => {
              const Icon = ICONS[value];
              return (
                <li key={value}>
                  <button
                    type="button"
                    aria-current={section === value ? 'page' : undefined}
                    className={`${styles.navItem} ${section === value ? styles.navItemOn : ''}`}
                    onClick={() => {
                      setSection(value);
                    }}
                  >
                    <Icon />
                    <span>{t(`boardSettings.${value}`)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
        <div className={styles.content}>
          {section === 'general' &&
            (canEdit ? (
              <BoardGeneralForm
                board={board}
                columns={columns}
                onSaved={onClose}
                onCancel={onClose}
              />
            ) : (
              <p>{t('boardSettings.readOnly')}</p>
            ))}
          {section === 'appearance' && (
            <div className={styles.stack}>
              <p>{t('boardSettings.appearanceHint')}</p>
              <div>
                <Button
                  variant="primary"
                  onClick={() => {
                    onClose();
                    onAppearance?.();
                  }}
                >
                  <Icons.palette /> {t('boards.appearance')}
                </Button>
              </div>
            </div>
          )}
          {section === 'labels' && board.workspace_id !== null && (
            <BoardLabelsSection
              boardId={board.id}
              workspaceId={board.workspace_id}
              canEditLabels={canEdit}
            />
          )}
          {section === 'access' && (
            <BoardAccessPanel board={board} role={role} permissions={permissions} />
          )}
          {section === 'advanced' && (
            <div className={styles.stack}>
              <OwnershipPanel board={board} workspaceId={isMember ? board.workspace_id : null} />
              {canTransfer && board.workspace_id !== null && (
                <section className={styles.block} aria-label={t('boardSettings.transfer')}>
                  <h3 className={styles.blockTitle}>{t('boardSettings.transfer')}</h3>
                  <BoardTransferSection
                    boardId={board.id}
                    boardName={board.name}
                    currentWorkspaceId={board.workspace_id}
                  />
                </section>
              )}
              {onExport && (
                <section className={styles.block} aria-label={t('portability.export.open')}>
                  <h3 className={styles.blockTitle}>{t('boardSettings.exportImport')}</h3>
                  <div>
                    <Button
                      onClick={() => {
                        onClose();
                        onExport();
                      }}
                    >
                      <Icons.export /> {t('portability.export.open')}
                    </Button>
                  </div>
                </section>
              )}
              {canDelete && (
                <section className={styles.block} aria-label={t('boards.delete')}>
                  <h3 className={styles.blockTitle}>{t('boardSettings.trash')}</h3>
                  <BoardDeleteSection board={board} onDeleted={onDeleted} />
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}
